import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../components/AdminLayout';
import MobileListCard from '../components/MobileListCard';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { apiErrorMessage } from '../lib/apiError';
import { useToast } from '../hooks/use-toast';
import { formatDateTime } from '../lib/dateFormat';

const KIND_LABELS = {
  back_in_stock: { label: 'Back in stock', color: '#16A34A' },
  out_of_stock: { label: 'Out of stock', color: '#DC2626' },
  stock_replenished: { label: 'Restocked', color: '#0EA5E9' },
  stock_drawn: { label: 'Stock drawn', color: '#EA580C' },
  stock_report: { label: 'Stock report', color: '#7C3AED' },
  inquiry: { label: 'Inquiry', color: '#F59E0B' },
  broadcast: { label: 'Broadcast', color: '#0078D4' },
};

const TRIGGER_LABELS = {
  inventory_upload: 'Inventory upload',
  single_edit: 'Single edit',
  manual: 'Manual',
  scheduled: 'Scheduled',
  auto_campaign: 'Auto campaign',
  subscriber: 'Subscriber notify',
};

const PAGE_SIZE = 50;

function fmtTs(iso) {
  return formatDateTime(iso);
}

export default function AdminNotificationsLog() {
  const { authHeaders } = useAdminAuth();
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterKind, setFilterKind] = useState('');
  const [filterTrigger, setFilterTrigger] = useState('');

  const load = useCallback(async () => {
    if (!authHeaders) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '200');
      if (filterKind) params.set('kind', filterKind);
      if (filterTrigger) params.set('trigger', filterTrigger);
      const { data } = await axios.get(
        `${API}/admin/notifications/dispatch-log?${params.toString()}`,
        { headers: authHeaders }
      );
      setRows(data.rows || []);
    } catch (err) {
      toast({ title: 'Could not load dispatch log', description: apiErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  }, [authHeaders, filterKind, filterTrigger, toast]);

  useEffect(() => { load(); }, [load]);

  // Summary tiles — counts by status across the visible rows
  const summary = useMemo(() => {
    const s = { total: rows.length, sent: 0, failed: 0, recipients: 0 };
    rows.forEach((r) => {
      s.sent += r.sent || 0;
      s.failed += r.failed || 0;
      s.recipients += r.recipient_count || 0;
    });
    return s;
  }, [rows]);

  return (
    <AdminLayout>
      <div className="px-4 sm:px-6 py-6 max-w-7xl mx-auto" data-testid="admin-notif-log-page">
        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Notification Dispatch Log</h1>
            <p className="text-sm text-neutral-500 mt-1">
              Every email &amp; SMS fan-out from stock changes, campaigns, and scheduled reports — newest first.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="px-4 h-9 rounded-md bg-[#0078D4] hover:bg-[#005EA6] text-white text-sm font-semibold disabled:opacity-50"
            data-testid="dispatch-log-refresh"
          >
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div className="rounded-lg border border-neutral-200 bg-white p-3" data-testid="tile-total">
            <div className="text-[10px] uppercase font-bold tracking-wider text-neutral-500">Dispatches</div>
            <div className="text-2xl font-bold text-neutral-900 mt-0.5">{summary.total}</div>
          </div>
          <div className="rounded-lg border border-neutral-200 bg-white p-3" data-testid="tile-recipients">
            <div className="text-[10px] uppercase font-bold tracking-wider text-neutral-500">Recipients</div>
            <div className="text-2xl font-bold text-neutral-900 mt-0.5">{summary.recipients}</div>
          </div>
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3" data-testid="tile-sent">
            <div className="text-[10px] uppercase font-bold tracking-wider text-emerald-700">Sent</div>
            <div className="text-2xl font-bold text-emerald-700 mt-0.5">{summary.sent}</div>
          </div>
          <div className="rounded-lg border border-rose-200 bg-rose-50 p-3" data-testid="tile-failed">
            <div className="text-[10px] uppercase font-bold tracking-wider text-rose-700">Failed</div>
            <div className="text-2xl font-bold text-rose-700 mt-0.5">{summary.failed}</div>
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap items-center gap-2 mb-4 text-xs">
          <span className="text-neutral-500 font-semibold uppercase tracking-wider mr-1">Kind:</span>
          <button
            className={`px-3 h-7 rounded-full border ${!filterKind ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-300 text-neutral-700 hover:bg-neutral-100'}`}
            onClick={() => setFilterKind('')}
            data-testid="filter-kind-all"
          >All</button>
          {Object.keys(KIND_LABELS).map((k) => (
            <button
              key={k}
              className={`px-3 h-7 rounded-full border ${filterKind === k ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-300 text-neutral-700 hover:bg-neutral-100'}`}
              onClick={() => setFilterKind(k)}
              data-testid={`filter-kind-${k}`}
            >
              {KIND_LABELS[k].label}
            </button>
          ))}
          <span className="text-neutral-500 font-semibold uppercase tracking-wider mr-1 ml-3">Trigger:</span>
          <button
            className={`px-3 h-7 rounded-full border ${!filterTrigger ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-300 text-neutral-700 hover:bg-neutral-100'}`}
            onClick={() => setFilterTrigger('')}
            data-testid="filter-trigger-all"
          >All</button>
          {Object.keys(TRIGGER_LABELS).map((t) => (
            <button
              key={t}
              className={`px-3 h-7 rounded-full border ${filterTrigger === t ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-300 text-neutral-700 hover:bg-neutral-100'}`}
              onClick={() => setFilterTrigger(t)}
              data-testid={`filter-trigger-${t}`}
            >
              {TRIGGER_LABELS[t]}
            </button>
          ))}
        </div>

        {/* Mobile card list — Feb 2026 native-app pass */}
        <div className="md:hidden space-y-2" data-testid="notif-log-list-mobile">
          {rows.length === 0 && !loading && (
            <div className="p-6 text-center text-neutral-400 text-sm">
              No dispatches yet. Trigger a back-in-stock, out-of-stock, or stock-report email to see entries here.
            </div>
          )}
          {rows.slice(0, PAGE_SIZE * 4).map((r, i) => {
            const k = KIND_LABELS[r.kind] || { label: r.kind, color: '#64748B' };
            return (
              <MobileListCard
                key={r.id || i}
                testId={`notif-log-card-${r.id || i}`}
                title={
                  <div className="flex items-center gap-2">
                    <span
                      className="px-2 py-0.5 rounded-full text-[10px] font-bold"
                      style={{ background: `${k.color}1A`, color: k.color }}
                    >
                      {k.label}
                    </span>
                    <span className="text-[11px] text-neutral-500">{TRIGGER_LABELS[r.trigger] || r.trigger}</span>
                  </div>
                }
                statusPill={
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-neutral-100 border border-neutral-200 text-neutral-600 capitalize">
                    {r.channel}
                  </span>
                }
                businessName={r.product_name || r.category_slug || '—'}
                customerName={`${r.recipient_count || 0} recip`}
                amount={
                  <span>
                    <span className="text-emerald-700">{r.sent || 0} sent</span>
                    {r.failed ? <span className="text-rose-600"> · {r.failed} fail</span> : null}
                  </span>
                }
                date={fmtTs(r.ts)}
              >
                {r.note && (
                  <div className="text-[11px] text-neutral-500 font-mono truncate" title={r.note}>{r.note}</div>
                )}
              </MobileListCard>
            );
          })}
        </div>

        {/* Table — hidden on <md */}
        <div className="hidden md:block rounded-lg border border-neutral-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-neutral-50 text-neutral-500 text-xs uppercase tracking-wider">
                  <th className="text-left py-2.5 px-3 font-semibold">When</th>
                  <th className="text-left py-2.5 px-3 font-semibold">Kind</th>
                  <th className="text-left py-2.5 px-3 font-semibold">Trigger</th>
                  <th className="text-left py-2.5 px-3 font-semibold">Channel</th>
                  <th className="text-left py-2.5 px-3 font-semibold">Product / Division</th>
                  <th className="text-right py-2.5 px-3 font-semibold">Recipients</th>
                  <th className="text-right py-2.5 px-3 font-semibold">Sent</th>
                  <th className="text-right py-2.5 px-3 font-semibold">Failed</th>
                  <th className="text-left py-2.5 px-3 font-semibold">Note</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && !loading && (
                  <tr><td colSpan={9} className="text-center py-10 text-neutral-400">
                    No dispatches yet. Trigger a back-in-stock, out-of-stock, or stock-report email to see entries here.
                  </td></tr>
                )}
                {rows.slice(0, PAGE_SIZE * 4).map((r, i) => {
                  const k = KIND_LABELS[r.kind] || { label: r.kind, color: '#64748B' };
                  return (
                    <tr key={r.id || i} className="border-t border-neutral-100 hover:bg-neutral-50/50">
                      <td className="py-2 px-3 font-mono text-[12px] text-neutral-600 whitespace-nowrap">{fmtTs(r.ts)}</td>
                      <td className="py-2 px-3">
                        <span
                          className="inline-block px-2 py-0.5 rounded-full text-[11px] font-semibold"
                          style={{ background: `${k.color}1A`, color: k.color }}
                        >
                          {k.label}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-neutral-700 text-[12px]">{TRIGGER_LABELS[r.trigger] || r.trigger}</td>
                      <td className="py-2 px-3 text-neutral-700 text-[12px] capitalize">{r.channel}</td>
                      <td className="py-2 px-3 text-neutral-800">
                        {r.product_name && <span className="font-medium">{r.product_name}</span>}
                        {r.category_slug && (
                          <span className="text-neutral-400 text-[11px] block">{r.category_slug}</span>
                        )}
                        {!r.product_name && !r.category_slug && <span className="text-neutral-400">—</span>}
                      </td>
                      <td className="py-2 px-3 text-right font-semibold text-neutral-800">{r.recipient_count || 0}</td>
                      <td className="py-2 px-3 text-right font-semibold text-emerald-700">{r.sent || 0}</td>
                      <td className="py-2 px-3 text-right font-semibold text-rose-600">{r.failed || 0}</td>
                      <td className="py-2 px-3 text-neutral-500 text-[11px] font-mono max-w-[260px] truncate" title={r.note}>{r.note || ''}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
